import { useEffect, useState } from "react";
import PricingCard from "./PricingCard";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { motion } from "framer-motion";

interface DbPlan {
  id: string;
  name: string;
  price: number;
  description: string;
  max_chit_funds: number;
  max_members: number;
  features: string[];
  created_at: string;
}

const PricingSection = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [dbPlans, setDbPlans] = useState<DbPlan[]>([]);
  
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const { data, error } = await supabase
          .from("plans")
          .select("*")
          .order("price", { ascending: true });
          
        if (error) {
          console.error("Error fetching plans:", error);
          toast.error("Failed to load pricing plans");
          return;
        }
        
        setDbPlans(data);
      } catch (error) {
        console.error("Error in fetchPlans:", error);
        toast.error("Failed to load pricing plans");
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPlans();
  }, []);
  
  // Map database plans to frontend plan format
  const plans = dbPlans.map(dbPlan => {
    // Parse features from JSON if needed
    let features = dbPlan.features;
    if (typeof features === 'string') {
      try {
        features = JSON.parse(features);
      } catch (e) {
        features = [];
      }
    }
    
    // Add standard features based on plan type
    if (dbPlan.name === "Basic") {
      features = [
        `${dbPlan.max_chit_funds} chit fund`,
        `Up to ${dbPlan.max_members} members`,
        "Basic reporting",
        "Manual reminders",
        "Email support",
      ];
    } else if (dbPlan.name === "Standard") {
      features = [
        `Up to ${dbPlan.max_chit_funds} chit funds`,
        `Up to ${dbPlan.max_members} members`,
        "Advanced reporting",
        "Automated SMS reminders",
        "Priority email support",
      ];
    } else if (dbPlan.name === "Premium") {
      features = [
        `${dbPlan.max_chit_funds} chit funds`,
        `${dbPlan.max_members} members`,
        "Custom reports & analytics",
        "WhatsApp & SMS reminders",
        "24/7 dedicated support",
      ];
    }
    
    return {
      name: dbPlan.name,
      price: dbPlan.price === 0 ? "Free" : `₹${dbPlan.price}/mo`,
      description: dbPlan.description || "",
      features: features,
      cta: "Get Started",
      highlighted: dbPlan.name === "Standard",
      planId: dbPlan.id
    };
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1,
        delayChildren: 0.3,
        duration: 0.5
      }
    }
  };

  return (
    <section id="pricing" className="py-24 px-4 md:px-8 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-muted/30 to-background"></div>
      <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/5 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-accent/5 rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto relative">
        <motion.div 
          initial={{ opacity: 0, y: 30 }} // Increased y from 20 to 30
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Simple, Transparent Pricing</h2>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto text-lg">
            Choose a plan that works for your chit fund management needs
          </p>
        </motion.div>
        
        {isLoading ? (
          <div className="flex justify-center">
            <div className="animate-spin h-10 w-10 border-4 border-primary border-t-transparent rounded-full"></div>
          </div>
        ) : (
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto"
          >
            {plans.map((plan, index) => (
              <PricingCard key={index} plan={plan} index={index} />
            ))}
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default PricingSection;
